/* The pieces the Stock Report's two tabs share: the design's chart frame
 * (a 52px tick gutter beside a bordered plot, labels underneath) and its
 * hover readout box. The plot's contents are the caller's — a polyline,
 * a row of bars — laid over the grid lines in the same box. */

import type { CSSProperties, ReactNode } from 'react'
import type { Tick } from './model'

/** Grid lines take the theme's `--c-grid` rather than the design's fixed
 *  neutral, as the other pages do, so they survive the dark theme. */
export function Frame({ height, ticks, labels, labelGap = 0, onLeave, children }: {
  height: number
  ticks: Tick[]
  /** One label per column, '' for a column left unlabelled. */
  labels?: string[]
  /** The gap between bar columns, so the labels sit under their bars. */
  labelGap?: number
  onLeave?: () => void
  children: ReactNode
}) {
  const grid: CSSProperties = { display: 'grid', gridTemplateColumns: '52px minmax(0,1fr)', gap: 8 }
  return (
    <>
      <div style={{ ...grid, marginTop: 18 }}>
        <div className="num" style={{ position: 'relative', height, fontSize: 11, color: 'var(--color-neutral-600)' }}>
          {ticks.map((t, i) => (
            <span key={i} style={{ position: 'absolute', right: 0, bottom: t.pos, transform: 'translateY(50%)' }}>
              {t.label}
            </span>
          ))}
        </div>
        <div onMouseLeave={onLeave} style={{
          position: 'relative', height,
          borderLeft: '1px solid var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-500)',
        }}>
          {ticks.map((t, i) => (
            <span key={i} style={{ position: 'absolute', left: 0, right: 0, bottom: t.pos, borderTop: '1px solid var(--c-grid)' }} />
          ))}
          {children}
        </div>
      </div>
      {labels ? (
        <div style={grid}>
          <div />
          <div style={{
            display: 'flex', gap: labelGap, padding: labelGap ? '5px 4px 0' : '5px 0 0',
            fontSize: 10, color: 'var(--color-neutral-600)',
          }}>
            {labels.map((d, i) => (
              <span key={i} style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}>{d}</span>
            ))}
          </div>
        </div>
      ) : null}
    </>
  )
}

/** The readout box beside a hovered column. `shift` flips it to the left
 *  of the column past the plot's middle so it never runs off the edge. */
export function Tip({ left, shift, minWidth = 170, children }: {
  left: string; shift: string; minWidth?: number; children: ReactNode
}) {
  return (
    <div style={{
      position: 'absolute', top: 8, left, transform: shift, pointerEvents: 'none', minWidth,
      padding: '10px 12px', background: 'var(--color-bg)', color: 'var(--color-text)',
      border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-md)', fontSize: 12, zIndex: 2,
    }}>{children}</div>
  )
}

/** The square swatch in front of a count or waste type. */
export function Swatch({ bg, size = 10 }: { bg: string; size?: number }) {
  return (
    <span style={{ display: 'inline-block', width: size, height: size, marginRight: 8, background: bg, flex: 'none' }} />
  )
}

/** A share bar in a track, with its figure beside it. */
export function ShareBar({ w, bg, share }: { w: string; bg: string; share: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ position: 'relative', flex: 1, height: 8, background: 'var(--c-track)' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: w, background: bg }} />
      </div>
      <span className="num" style={{ fontSize: 12, minWidth: 40, textAlign: 'right' }}>{share}</span>
    </div>
  )
}

export const PANEL: CSSProperties = { padding: '22px 24px 22px' }
export const RIGHT: CSSProperties = { textAlign: 'right' }
